import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import React, { useMemo, useState } from "react";

import { BinaryImageActions } from "features/answerPanel/components/BinaryImageActions";
import { CountImageActions } from "features/answerPanel/components/CountImageActionsNew";
import { MultichoiceImageActions } from "features/answerPanel/components/MultichoiceImageActions";
import { TextRecognitionImageActions } from "features/answerPanel/components/TextRecognitionImageActions";
import { DetectorModes } from "types/createDetector";
import { ModeConfiguration } from "types/detectors";
import { CanvasAnnotation } from "types/imageAnnotations";

export interface RelabelActionsViewProps {
  containerId: string;
  isLoading: boolean;
  onClose: () => void;
  posicheckId?: string;
  lastLabel?: string | null;
  mode: DetectorModes;
  modeConfig?: ModeConfiguration;
  onLabelSubmit: (label: string | number, canvasAnnotations?: CanvasAnnotation[]) => void;
  onUnclearSubmit: (feedbackText: string) => void;
  rois: CanvasAnnotation[];
  showCloseButton?: boolean;
  closeButtonTopPosition?: string;
  circularProgressSize?: number;
  showDeleteButton?: boolean;
  onDeleteImageQuery?: () => void | Promise<void>;
  isDeletingImageQuery?: boolean;
}

export function RelabelActionsView(props: RelabelActionsViewProps) {
  const {
    containerId,
    isLoading,
    onClose,
    posicheckId,
    lastLabel,
    mode,
    modeConfig,
    onLabelSubmit,
    onUnclearSubmit,
    rois,
    showCloseButton = true,
    closeButtonTopPosition = "9px",
    circularProgressSize = 35,
    showDeleteButton = false,
    onDeleteImageQuery,
    isDeletingImageQuery = false,
  } = props;

  const [unclearMenuOpen, setUnclearMenuOpen] = useState(false);

  const actionComponent = useMemo(() => {
    if (!posicheckId) {
      return null;
    }
    const commonProps = {
      imageQueryId: posicheckId,
      isUnclearMenuOpen: unclearMenuOpen,
      setUnclearMenuOpen,
      onLabelSubmit,
      onUnclearSubmit,
      showDeleteButton,
      onDeleteImageQuery,
      isDeletingImageQuery,
    };

    if (!modeConfig) {
      return <BinaryImageActions {...commonProps} />;
    }

    switch (mode) {
      case DetectorModes.MULTICHOICE:
        return <MultichoiceImageActions {...commonProps} choices={modeConfig.class_names || []} />;
      case DetectorModes.TEXT_RECOGNITION:
        return (
          <TextRecognitionImageActions
            {...commonProps}
            defaultLabel={lastLabel || ""}
            valueMaxLength={modeConfig.value_max_length}
          />
        );
      case DetectorModes.COUNT:
      case DetectorModes.BOUNDING_BOX:
        return (
          <CountImageActions
            {...commonProps}
            maxCount={modeConfig.max_count || modeConfig.max_num_bboxes || 25}
            annotations={rois}
            isCountMode={mode === DetectorModes.COUNT}
          />
        );
      default:
        return null;
    }
  }, [
    posicheckId,
    unclearMenuOpen,
    modeConfig,
    mode,
    onLabelSubmit,
    onUnclearSubmit,
    lastLabel,
    rois,
    showDeleteButton,
    onDeleteImageQuery,
    isDeletingImageQuery,
  ]);

  return (
    <Box id={containerId} width="100%" display="flex" alignItems="center" justifyItems="center">
      {isLoading || !posicheckId ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100px" }}>
          <CircularProgress size={circularProgressSize} className="ml-2" />
        </Box>
      ) : (
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          {showCloseButton && (
            <Tooltip title="Close">
              <IconButton
                id="close-button"
                aria-label="close"
                onClick={onClose}
                sx={{
                  zIndex: 10,
                  position: "absolute",
                  top: closeButtonTopPosition,
                  right: "15px",
                  display: "flex",
                  width: "15px",
                  height: "15px",
                  bgcolor: "#C8CDD3",
                  borderRadius: "15px",
                }}
              >
                <CloseIcon
                  sx={{
                    width: "15px",
                    height: "15px",
                    color: "#49494D",
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {actionComponent}
        </Box>
      )}
    </Box>
  );
}
